import styled from '@emotion/styled';
import { Icon } from '@mdi/react';
import { BaseButton, forPhoneOnly, Variables } from '../../base';
import { TitleCard } from '../../card';
import { DataTable } from '../../data-table';
import { styledTitleCardSectionTitle } from '../claim-section.styles';

export const StyledTitleCard = styled(TitleCard)`
    ${styledTitleCardSectionTitle}
    justify-content: space-between;
    max-width: 100%;
`;

export const CardBody = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 100%;
    overflow-x: auto;
`;

export const StyledDataTable = styled(DataTable)`
    display: flex;
    flex-direction: column;
    margin: 12px 24px;
    flex: 1 1 100%;
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 4px;

    [role='columnheader'] {
        padding: 8px 12px 6px 12px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-weight: 600;
        font-size: 12px;
        border-right: none;
        border-bottom: 1px solid ${Variables.ThemeLight.Border1};
        background-color: ${Variables.Colors.n5};
    }
    [role='cell'] {
        display: inline-block;
        padding: 8px 12px 6px 12px;
        line-height: 24px;
        font-size: 14px;
        color: ${Variables.Colors.n70};
        border-bottom: 1px solid ${Variables.ThemeLight.Border1};

        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        &:first-of-type {
            text-align: center;
            padding: 8px;
        }
    }

    [role='row']:last-of-type [role='cell'] {
        border-bottom: none;
    }

    ${forPhoneOnly(`
        [role='cell'] {
            &:first-of-type {
                padding-left: 8px;
            }
        }
    `)}
`;

export const StyledTag = styled.span`
    display: inline-block;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.02em;
    white-space: nowrap;
    border-radius: ${Variables.Rounded.medium};
    padding: 2px 6px;
    color: ${Variables.ThemeLight.TextMedium};
    background-color: ${Variables.ThemeLight.Background1};
    border: 1px solid ${Variables.ThemeLight.Border1};
    &:not(:first-of-type) {
        margin-left: 4px;
    }
`;

export const MismatchDescriptionTag = styled.button<{ $clickable?: boolean }>`
    display: inline-block;
    color: ${Variables.ThemeLight.PrimaryOpacity10};
    background-color: ${Variables.ThemeLight.PrimaryMild2};
    border: 1px solid ${Variables.ThemeLight.PrimaryMild1};
    border-radius: ${Variables.Rounded.medium};
    padding: 2px 8px;
    font-size: 13px;
    line-height: 1.4;
    white-space: normal;
    text-align: left;
    cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

    &:hover {
        background-color: ${({ $clickable }) =>
            $clickable ? Variables.ThemeLight.PrimaryMild1 : Variables.ThemeLight.PrimaryMild2};
    }
`;

export const NoLikelyMatchLabel = styled.span`
    font-size: 14px;
    font-style: italic;
    color: ${Variables.ThemeLight.TextXMild};
`;

export const MismatchEyeIcon = styled(Icon)<{ $headerRow?: boolean }>`
    width: 18px;
    height: 18px;
    color: ${({ $headerRow }) =>
        $headerRow ? Variables.ThemeLight.AttentionConstant6 : Variables.ThemeLight.TextXMild};
    vertical-align: middle;
    flex-shrink: 0;
`;

export const TotalLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    color: ${Variables.Colors.n70};
`;

export const StyledDescription = styled.span`
    line-height: 1.5;
    color: ${Variables.Colors.n100};
`;

export const StyledCharges = styled.span<{ $isZero?: boolean }>`
    display: block;
    text-align: right;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    color: ${({ $isZero }) => ($isZero ? Variables.ThemeLight.TextXMild : 'inherit')};
    &:focus-visible {
        outline: 2px solid ${Variables.ThemeLight.PrimaryInverted3};
        outline-offset: 2px;
    }
`;

export const EmptyValueWrapper = styled.div`
    width: 100%;
    text-align: right;
`;

export const ReasonCodesWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
`;

export const ReasonChip = styled.span<{ $patientResponsibility?: boolean }>`
    display: inline-block;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.02em;
    white-space: nowrap;
    border-radius: ${Variables.Rounded.medium};
    padding: 2px 6px;
    cursor: help;
    &:focus-visible {
        outline: 2px solid ${Variables.ThemeLight.PrimaryInverted3};
        outline-offset: 2px;
    }
    color: ${({ $patientResponsibility }) =>
        $patientResponsibility ? Variables.ThemeLight.AttentionInverted3 : Variables.ThemeLight.TextMedium};
    background-color: ${({ $patientResponsibility }) =>
        $patientResponsibility ? Variables.ThemeLight.AttentionMild3 : Variables.ThemeLight.Background1};
    border: 1px solid
        ${({ $patientResponsibility }) =>
            $patientResponsibility ? Variables.ThemeLight.AttentionMild1 : Variables.ThemeLight.Border1};
`;

export const NoReasonCodesLabel = styled.span`
    display: block;
    text-align: center;
    color: ${Variables.ThemeLight.TextXMild};
`;

export const NetVsContractWrapper = styled.div<{ $state: 'at' | 'under' | 'over' }>`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    cursor: help;
    &:focus-visible {
        outline: 2px solid ${Variables.ThemeLight.PrimaryInverted3};
        outline-offset: 2px;
    }
    color: ${({ $state }) =>
        $state === 'under'
            ? Variables.ThemeLight.WarningInverted3
            : $state === 'over'
              ? Variables.ThemeLight.AttentionInverted3
              : Variables.ThemeLight.SuccessInverted3};
`;

export const NetVsContractAmount = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;

    svg {
        width: 14px;
        height: 14px;
        fill: currentColor;
    }
`;

export const NetVsContractBadge = styled.span<{ $state: 'at' | 'under' | 'over' }>`
    display: inline-flex;
    align-items: center;
    height: 20px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
    border-radius: ${Variables.Rounded.small};
    padding: 0 5px;
    border: 1px solid;
    border-color: ${({ $state }) =>
        $state === 'under'
            ? Variables.ThemeLight.WarningInverted3
            : $state === 'over'
              ? Variables.ThemeLight.AttentionInverted3
              : Variables.ThemeLight.SuccessInverted3};
    color: ${({ $state }) =>
        $state === 'under'
            ? Variables.ThemeLight.WarningInverted3
            : $state === 'over'
              ? Variables.ThemeLight.AttentionInverted3
              : Variables.ThemeLight.SuccessInverted3};
    background-color: ${({ $state }) =>
        $state === 'under'
            ? Variables.ThemeLight.WarningMild3
            : $state === 'over'
              ? Variables.ThemeLight.AttentionMild3
              : Variables.ThemeLight.SuccessMild3};
`;

export const Header = styled.div<{ align?: 'left' | 'right' }>`
    text-align: ${({ align }) => (align === 'right' ? 'right' : 'left')};
`;

export const StyledIcon = styled(Icon)`
    height: 24px;
    width: 24px;
    vertical-align: middle;
    fill: ${Variables.Colors.nx24};
`;

export const ExpandableDiv = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    flex-grow: 1;

    cursor: pointer;
    padding: 0;
`;

export const ExpandIcon = styled(Icon)`
    margin-top: 4px;
    margin-right: 16px;
    height: 16px;
    width: 16px;
    fill: ${Variables.Colors.b300};
    align-self: flex-end;
`;

export const AlignCell = styled.div`
    display: flex;
    justify-content: center;
`;

export const SuccessIcon = styled(Icon)`
    margin-top: 4px;
    height: 16px;
    width: 16px;
    fill: ${Variables.Colors.successful};
    align-self: flex-end;
`;

export const WarningIcon = styled(Icon)`
    margin-top: 4px;
    height: 16px;
    width: 16px;
    fill: ${Variables.Colors.warning};
    align-self: flex-end;
`;

export const Expand = styled.div`
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: space-around;
    flex-grow: 1;
    padding: 0 8px;
`;

export const DetailsTable = styled.table`
    flex-grow: 0;
    padding: 12px 24px;
    & th {
        white-space: nowrap;
        text-align: left;
        font-weight: 500;
    }
    & td span {
        display: inline-block;
        font-size: 16px;
        text-decoration: underline;
    }
`;

export const ProviderTable = styled.table`
    display: inline-flex;
    flex-grow: 0;
    max-width: 400px;
    margin-left: 24px;
`;
export const ProviderType = styled.th`
    text-align: right;
    font-size: 15px;
    font-weight: 500;
`;
export const ProviderName = styled.td`
    text-align: left;
    font-size: 15px;
    padding-left: 12px;
`;

export const FooterLink = styled.a`
    display: block;
    text-align: right;
    padding: 0 24px;
    flex-grow: 1;
    text-align: right;
    font-size: 15px;
    font-weight: 500;
    letter-spacing: normal;
    line-height: 1;
    cursor: pointer;
    color: ${Variables.Colors.p300};
`;

export const RowWrapper = styled.div<{ warning: boolean }>`
    > div:first-of-type {
        background-color: ${({ warning }) => (warning ? Variables.Colors.n5 : undefined)};
    }
`;

export const MismatchGroupRow = styled.button`
    width: 100%;
    border: none;
    background-color: ${Variables.ThemeLight.AttentionOpacity3};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 15px;
`;

export const MismatchGroupLeft = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

export const MismatchGroupText = styled.span`
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    min-width: 0;
    text-align: left;
`;

export const MismatchGroupTitle = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${Variables.Colors.n70};
    white-space: nowrap;
`;

export const MismatchGroupContext = styled.span`
    font-size: 12px;
    font-weight: 400;
    color: ${Variables.ThemeLight.TextMild};
`;

export const MismatchGroupRight = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: auto;
`;

export const MismatchGroupAmount = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${Variables.Colors.n70};
    white-space: nowrap;
`;

export const MismatchChevron = styled(Icon)`
    width: 16px;
    height: 16px;
    fill: ${Variables.Colors.n50};
    flex-shrink: 0;
`;

export const SummaryRow = styled.div`
    background-color: ${Variables.Colors.b300_l96};
    [role='cell'] {
        font-size: 16px;
        font-weight: 500;
        border-bottom: none;
    }
`;

export const ResourceTable = styled.section`
    width: 100%;
    padding: 10px;
    box-shadow: ${Variables.Shadows.border};
    margin: 16px 0;
`;

export const EmptyLabel = styled.span`
    color: ${Variables.Colors.n30};
    text-decoration: none !important;
`;

export const MutedStatusLabel = styled.span`
    color: ${Variables.Colors.n30};
    font-size: 13px;
    white-space: nowrap;
    text-decoration: none !important;
`;

export const PresentStatusLabel = styled.span`
    color: ${Variables.Colors.n50};
    font-size: 13px;
    white-space: nowrap;
    text-decoration: none !important;
`;

export const ActionButton = styled(BaseButton)`
    min-width: auto;
    fill: ${Variables.Colors.n30};
    height: 30px;
    width: 40px;
    border-color: transparent;
    background: none;

    &:hover {
        fill: ${Variables.Colors.n70};
        /* background: white; */
        border: solid 1px ${Variables.Colors.nx8};
    }

    padding: 0;
    z-index: 1;
`;
